import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Modal from '../../../../common/Modal';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import { employmentApi } from '../../../../../services/api';
import { canConfirmDeletion, deletionConfirmText, fmtDay as fmt } from '../../../../../utils/overlappingPositionLegs';

/**
 * ADR-165 A5 — delete a contract (employment episode) to correct data.
 *
 * Loads the preview (writes nothing), shows what goes, the blockers (Delete
 * disabled) and the warnings, and enables Delete only once the operator
 * retypes the contract's start date — several cards can look alike.
 */
const DeleteEpisodeDialog = ({ episodeId, onClose, onDeleted }) => {
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setLoadError('');
    setConfirmText('');
    employmentApi.previewEpisodeDeletion(episodeId)
      .then((data) => { if (!cancelled) setPreview(data); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || 'Failed to load the contract'); });
    return () => { cancelled = true; };
  }, [episodeId]);

  const expected = deletionConfirmText(preview);
  const confirmed = canConfirmDeletion(preview, confirmText);
  const blocked = Boolean(preview) && !preview.canDelete;

  const submit = async () => {
    if (!confirmed || blocked) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      await employmentApi.deleteEmploymentEpisode(episodeId);
      onDeleted();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete the contract');
    } finally {
      setIsDeleting(false);
    }
  };

  const ep = preview?.episode;
  const label = ep ? [ep.contractType || 'Contract', ep.contractCompany].filter(Boolean).join(' — ') : '';

  return (
    <Modal
      isOpen
      title="Delete contract"
      onClose={onClose}
      onCancel={onClose}
      onSubmit={submit}
      submitText={isDeleting ? 'Deleting...' : 'Delete contract'}
      submitDisabled={!preview || blocked || !confirmed || isDeleting}
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ mt: 1 }} data-testid="delete-episode-dialog">
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {!preview && !loadError && <LoadingSpinner />}

        {preview && (
          <>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                {fmt(ep.startDate)} - {fmt(ep.endDate)}{ep.source ? ` · source: ${ep.source}` : ''}
              </Typography>
            </Box>

            <Alert severity="info">
              This permanently removes the contract, its {preview.removes.events} event(s) and its{' '}
              {preview.removes.changeRecords} change record(s). Positions, grades and the hire date are not changed.
              Nothing records the deletion in the data.
            </Alert>

            {preview.blockers.map((b) => (
              <Alert key={b.code} severity="error" data-testid={`blocker-${b.code}`}>{b.message}</Alert>
            ))}
            {preview.warnings.map((w) => (
              <Alert key={w.code} severity="warning" data-testid={`warning-${w.code}`}>
                {w.message}
                {w.legs && (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                    {w.legs.map((l) => (
                      <li key={l.id}>{l.position || 'Position'}: {fmt(l.startDate)} - {fmt(l.endDate)}</li>
                    ))}
                  </Box>
                )}
              </Alert>
            ))}

            {preview.removes.eventList.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Event</TableCell>
                    <TableCell>Effective</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.removes.eventList.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{e.type}</TableCell>
                      <TableCell>{e.effectiveOn ? fmt(e.effectiveOn) : '-'}</TableCell>
                      <TableCell>{e.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!blocked && (
              <TextField
                label={`Type the start date (${expected}) to confirm`}
                size="small"
                fullWidth
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={isDeleting}
                inputProps={{ 'data-testid': 'delete-episode-confirm' }}
              />
            )}
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </>
        )}
      </Stack>
    </Modal>
  );
};

DeleteEpisodeDialog.propTypes = {
  episodeId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired
};

export default DeleteEpisodeDialog;
